import * as React from "react";
import { cn } from "@/lib/utils";

// Lightweight, Radix-free tooltip implementation to avoid provider/runtime issues

// No-op provider to keep API compatibility
const TooltipProvider: React.FC<React.PropsWithChildren<{ delayDuration?: number }>> = ({ children }) => (
  <>{children}</>
);

// Internal context to share open state between Trigger and Content
interface TooltipContextValue {
  open: boolean;
  setOpen: (v: boolean) => void;
}
const TooltipContext = React.createContext<TooltipContextValue | null>(null);

const Tooltip: React.FC<React.PropsWithChildren<{ defaultOpen?: boolean }>> = ({ children, defaultOpen = false }) => {
  const [open, setOpen] = React.useState(defaultOpen);
  return (
    <TooltipContext.Provider value={{ open, setOpen }}>
      <span className="relative inline-flex items-center">{children}</span>
    </TooltipContext.Provider>
  );
};

interface TriggerProps extends React.HTMLAttributes<HTMLElement> {
  asChild?: boolean;
  children: React.ReactNode;
}

const TooltipTrigger: React.FC<TriggerProps> = ({ asChild, children, ...props }) => {
  const ctx = React.useContext(TooltipContext);
  const triggerProps = {
    onMouseEnter: () => ctx?.setOpen(true),
    onMouseLeave: () => ctx?.setOpen(false),
    onFocus: () => ctx?.setOpen(true),
    onBlur: () => ctx?.setOpen(false),
    ...props,
  } as React.HTMLAttributes<HTMLElement>;

  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(children as React.ReactElement, {
      ...triggerProps,
      ...((children as any).props || {}),
      onMouseEnter: (e: any) => {
        (children as any).props?.onMouseEnter?.(e);
        triggerProps.onMouseEnter?.(e);
      },
      onMouseLeave: (e: any) => {
        (children as any).props?.onMouseLeave?.(e);
        triggerProps.onMouseLeave?.(e);
      },
      onFocus: (e: any) => {
        (children as any).props?.onFocus?.(e);
        triggerProps.onFocus?.(e);
      },
      onBlur: (e: any) => {
        (children as any).props?.onBlur?.(e);
        triggerProps.onBlur?.(e);
      },
    });
  }

  return (
    <span {...triggerProps}>
      {children}
    </span>
  );
};

interface ContentProps extends React.HTMLAttributes<HTMLDivElement> {
  sideOffset?: number;
  side?: "top" | "bottom" | "left" | "right";
  align?: "start" | "center" | "end";
}

const TooltipContent = React.forwardRef<HTMLDivElement, ContentProps>(
  ({ className, sideOffset = 4, style, children, ...props }, ref) => {
    const ctx = React.useContext(TooltipContext);
    if (!ctx?.open) return null;

    return (
      <div
        ref={ref}
        role="tooltip"
        style={{ marginTop: sideOffset, ...style }}
        className={cn(
          "pointer-events-none absolute left-1/2 top-full -translate-x-1/2 z-50 rounded-md border bg-popover px-3 py-1.5 text-sm text-popover-foreground shadow-md",
          "animate-in fade-in-0 zoom-in-95",
          className
        )}
        {...props}
      >
        {children}
      </div>
    );
  }
);
TooltipContent.displayName = "TooltipContent";

// Radix-compatible re-exports (for any usage expecting @radix-ui/react-tooltip)
const Provider = TooltipProvider;
const Root = Tooltip;
const Trigger = TooltipTrigger;
const Content = TooltipContent;

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider, Provider, Root, Trigger, Content };
